import { clamp01, smoothstep } from '../core/math';
import type { GuardState, PunchEvent } from '../recog/events';
import type { HitResult, IncomingAttack, MatchMode } from './types';

/**
 * HIT RESOLUTION UNDER MONOCULAR UNCERTAINTY
 * ==========================================
 *
 * We cannot measure depth. Anyone who claims otherwise from a single RGB webcam
 * is either using apparent-size scaling (which we do, and which is a *cue*, not a
 * measurement) or is wrong. So the design does not pretend to a geometric
 * collision test. Instead:
 *
 *   1. Every geometric term is FUZZY. `range`, `lateral` and `height` return
 *      continuous 0..1 memberships, not booleans. There is no coordinate at which
 *      a punch flips from certain-hit to certain-miss.
 *
 *   2. UNCERTAINTY WIDENS THE PLAYER'S HITBOX, NEVER NARROWS IT. When pose
 *      confidence drops, the tolerance bands *expand*. A missed hit that should
 *      have landed feels like the game is broken; a generous hit feels like the
 *      game is on your side. Given that the error is unavoidable, we spend it in
 *      the direction that preserves trust. Score magnitude still scales with
 *      confidence, so low-confidence hits are worth less — the player is never
 *      cheated, just less rewarded.
 *
 *   3. TEMPORAL CONSISTENCY DOES THE HEAVY LIFTING. A punch only reaches this
 *      code after the FSM has seen a coherent extend-peak trajectory across
 *      several frames. Single-frame landmark spikes cannot produce a hit.
 *
 *   4. INTENT BEATS GEOMETRY. Whether a slip beats a cross is decided by *timing
 *      relative to the punch*, which we observe well, rather than by whether the
 *      head volume overlaps the fist volume, which we observe badly.
 *
 * Alternatives considered and rejected:
 *   - Pure 2-D screen overlap: ignores the single most important boxing variable
 *     (range) and rewards waving at the camera.
 *   - Trusting BlazePose world-z directly: z error on the wrist is comparable to
 *     the entire punch travel we are trying to measure. Usable as a *relative*
 *     signal within one frame's body, useless as an absolute distance.
 *   - Requiring a second camera or depth sensor: kills the "any laptop" promise,
 *     which is the product's whole reason to exist.
 */

export interface RangeContext {
  mode: MatchMode;
  /** Distance from the player's hip plane to the opponent, sw. */
  gap: number;
  /** Opponent's lateral offset from the player's centre line, sw. */
  lateral: number;
  /** Opponent head height relative to the player's shoulder line, sw. Negative = they ducked. */
  targetHeadHeight: number;
  /** Overall pose confidence this frame, 0..1. */
  poseConfidence: number;
  /** How stable the depth estimate has been recently, 0..1. Low = widen everything. */
  depthStability: number;
  /**
   * Signed change in gap since the AI committed to `atk`, sw.
   * Negative means the player retreated out of range during the wind-up —
   * distance management as an active defence. Supplied by the match sim so this
   * module stays free of simulation state.
   */
  gapDelta(atk: IncomingAttack): number;
}

export interface DefenderState {
  /** Guard quality on the side the punch is arriving at, 0..1. */
  guardOnLine: number;
  /**
   * True when the defender is *actively* blocking rather than merely carrying a
   * high guard. A passive guard should absorb far less than a deliberate catch —
   * otherwise a fighter who simply stands with their hands up nullifies half of
   * everything thrown at them, which is neither realistic nor fun to attack.
   */
  activelyBlocking: boolean;
  shell: number;
  /** Active evasion and how committed it is. */
  evading: string | null;
  evasionDepth: number;
  /** Signed lateral head displacement, sw. */
  headOffX: number;
  headOffY: number;
  headOffZ: number;
  /** True while the defender is mid-punch — the classic counter opening. */
  committed: boolean;
  /** 0..1 stamina; tired fighters carry their hands low. */
  stamina: number;
}

/** Widening factor applied to every tolerance band when we are unsure. */
function slack(ctx: RangeContext): number {
  const unsure = 1 - clamp01(ctx.poseConfidence * 0.6 + ctx.depthStability * 0.4);
  return 1 + 0.55 * unsure; // up to +55% tolerance when tracking is poor
}

/**
 * Player punch -> opponent. Returns a graded outcome, never a coin flip: the same
 * inputs always produce the same result, because "the game rolled against you" is
 * unreadable feedback in a skill trainer.
 */
export function resolvePlayerPunch(p: PunchEvent, ctx: RangeContext, d: DefenderState): HitResult {
  const s = slack(ctx);

  // --- Range / commitment -------------------------------------------------
  let range: number;
  if (ctx.mode === 'punch-out') {
    // Fixed range: distance is not part of the game, so the "did you reach?"
    // question is replaced by "did you actually commit to the punch?".
    // Extension is measured against the player's OWN calibrated arm length, so
    // it is scale-free and — unlike depth — something the camera sees reliably.
    // Lazy half-extended arm punches still score badly; they just fail for an
    // honest reason instead of a guessed one.
    range = smoothstep(0.5, 0.78, p.extension);
  } else {
    // `p.reach` is how far in front of the hip plane the fist got, in sw.
    // Positive `over` means the fist travelled past the target plane.
    const over = p.reach - ctx.gap;
    const tooShort = smoothstep(-0.3 * s, 0.02, over);
    // Past a point you are inside your own punch: arms fold, nothing lands.
    const smothered = 1 - smoothstep(0.55 * s, 1.0 * s, over);
    range = tooShort * smothered;
  }

  // --- Lateral alignment --------------------------------------------------
  const lateralError = Math.abs(p.lateral - ctx.lateral);
  const lateralTol = (p.target === 'head' ? 0.44 : 0.62) * s;
  const lateral = 1 - smoothstep(lateralTol, lateralTol * 2.1, lateralError);

  // --- Height -------------------------------------------------------------
  // Measured against where the opponent's head actually is *right now*, which is
  // how ducking works mechanically: drop the head and head shots stop connecting.
  const wantHeight = p.target === 'head' ? ctx.targetHeadHeight : ctx.targetHeadHeight - 0.85;
  const heightError = Math.abs(p.height - wantHeight);
  const height = 1 - smoothstep(0.34 * s, 0.8 * s, heightError);

  // --- Defence ------------------------------------------------------------
  let defence = 1;
  let defeatedBy: string | undefined;

  if (d.evading && d.evasionDepth > 0.35) {
    // A slip only works if the head moved *off the line of that punch*. Slipping
    // into a hook is a mistake, and the model says so.
    const movedOffLine = evasionEffectiveness(p, d);
    const reduce = movedOffLine * d.evasionDepth;
    if (reduce > 0.25) {
      defence *= 1 - 0.92 * reduce;
      defeatedBy = d.evading;
    }
  }
  const guardStop =
    d.guardOnLine * (0.55 + 0.45 * d.shell) * (d.activelyBlocking ? 1 : 0.55) * (p.target === 'head' ? 1 : 0.72);
  if (guardStop > 0.35) {
    defence *= 1 - 0.8 * guardStop;
    if (!defeatedBy) defeatedBy = 'guard';
  }
  // Uppercuts go through a high guard; hooks go around a straight-on shell.
  if (p.type === 'uppercut') defence = Math.min(1, defence / (0.55 + 0.45 * (1 - d.shell)) ** 0.5 + 0.18);
  if (p.type === 'hook' && d.shell > 0.7) defence *= 1.12;

  // --- Openings -----------------------------------------------------------
  // Countering a committed opponent is the highest-value action in boxing, and
  // the model rewards it explicitly rather than leaving it to emerge by accident.
  const opening = d.committed ? 1.25 : 1.0;

  const confidence = clamp01(p.confidence * (0.55 + 0.45 * ctx.poseConfidence));
  const raw = range * lateral * height * defence * opening;
  const quality = clamp01(raw);

  let outcome: HitResult['outcome'];
  if (quality >= 0.6) outcome = 'clean';
  else if (quality >= 0.28) outcome = 'glancing';
  else if (defeatedBy === 'guard') outcome = 'blocked';
  else if (defeatedBy) outcome = 'slipped';
  else outcome = 'miss';

  return {
    outcome,
    quality: quality * (0.62 + 0.38 * confidence),
    terms: { range, lateral, height, defence, opening, confidence },
    defeatedBy,
  };
}

/**
 * How well a given evasion beats a given punch. This is the table that makes
 * head movement a *skill* rather than a panic button: every evasion has punches
 * it beats and punches it walks into.
 */
function evasionEffectiveness(p: PunchEvent, d: DefenderState): number {
  const lateralAway = -Math.sign(p.lateral || 0.001) * Math.sign(d.headOffX || 0.001);
  const movedAway = clamp01(Math.abs(d.headOffX) / 0.35) * (lateralAway > 0 ? 1 : 0.15);

  switch (d.evading) {
    case 'slip':
      // Beats straights cleanly; walks into hooks on the side you slipped toward.
      return p.type === 'jab' || p.type === 'cross' || p.type === 'overhand'
        ? 0.55 + 0.45 * movedAway
        : p.type === 'hook'
          ? 0.15 * movedAway
          : 0.25;
    case 'duck':
      // Beats everything aimed at the head; useless against the body, and the
      // classic punishment for ducking is the uppercut.
      if (p.target === 'body') return 0.05;
      return p.type === 'uppercut' ? 0.1 : 0.85;
    case 'roll':
      return p.type === 'hook' ? 0.9 : p.type === 'uppercut' ? 0.2 : 0.5;
    case 'pull':
      // Taking your head off the end of a punch. It is THE answer to an
      // uppercut — you cannot duck one and slipping barely helps — and it is a
      // solid answer to straights. It is weak against hooks, which follow you.
      //
      // This previously returned 0.3 against uppercuts while the HUD instructed
      // the player to "LEAN BACK" against exactly that punch. Following the
      // on-screen prompt got you hit, and nothing in the whole matchup table
      // beat an uppercut, making it an unanswerable attack rather than a hard one.
      if (p.type === 'uppercut') return 0.85;
      if (p.type === 'jab' || p.type === 'cross') return 0.7;
      return p.type === 'hook' ? 0.25 : 0.4;
    case 'lean':
      return 0.45;
    default:
      return 0.2;
  }
}

/**
 * Opponent punch -> player. Evaluated at `tImpact` against the player's *current*
 * observed state, which is exactly the reaction test we want: the player had from
 * `tTelegraph` to `tImpact` to do something about it.
 */
export function resolveIncoming(
  atk: IncomingAttack,
  guard: GuardState,
  ctx: RangeContext,
  playerStamina: number,
): HitResult {
  const s = slack(ctx);

  if (atk.isFeint) {
    return {
      outcome: 'miss',
      quality: 0,
      terms: { range: 0, lateral: 0, height: 0, defence: 1, opening: 1, confidence: ctx.poseConfidence },
      defeatedBy: 'feint',
    };
  }

  // Range: in free-spar the AI only commits to punches it believes are in range,
  // so this term punishes a player who stepped out during the wind-up. At fixed
  // range there is nowhere to step, so backing out is not a defence and the term
  // is removed rather than left to produce noise from an unreliable depth cue.
  const range =
    ctx.mode === 'punch-out' ? 1 : 1 - smoothstep(0.15 * s, 0.75 * s, Math.max(0, -ctx.gapDelta(atk)));

  const lateralError = Math.abs(atk.lane - guard.headOffX);
  const lateralTol = (atk.target === 'head' ? 0.42 : 0.6) * s;
  const lateral = 1 - smoothstep(lateralTol, lateralTol * 2.0, lateralError);

  const wantHeight = atk.height;
  const playerHeadDrop = guard.headOffY;
  const heightError = Math.abs(wantHeight - playerHeadDrop * (atk.target === 'head' ? 1 : 0.2));
  const height = 1 - smoothstep(0.36 * s, 0.85 * s, heightError);

  let defence = 1;
  let defeatedBy: string | undefined;

  if (ctx.mode === 'punch-out') {
    // At fixed range the defensive question is purely: did you pick a movement
    // that beats THIS punch, and did it peak on the punch?
    //
    // The free-spar model reduced a correct slip to only ~0.44 quality — still a
    // glancing hit — because it leaned on a lateral-offset term whose tolerance
    // band is far wider than a slip's actual displacement. That is fine when
    // range is also in play, and completely wrong here: in a reaction trainer a
    // correct, well-timed dodge must cleanly miss, or the player gets no signal
    // that they did the right thing.
    const kind = guard.evading ?? guard.lastEvasionKind;
    if (kind) {
      const eff = incomingEvasionEffectiveness(atk, { ...guard, evading: kind, headOffX: guard.lastEvasionOffX });
      const timingError = guard.lastEvasionPeakT > 0 ? Math.abs(guard.lastEvasionPeakT - atk.tImpact) : 1e6;
      const timing = 1 - smoothstep(150, 480, timingError);
      const depth = clamp01(guard.lastEvasionDepth / 0.3);
      const stop = eff * timing * depth;
      if (stop > 0.12) {
        defence *= 1 - 0.97 * stop;
        defeatedBy = kind;
      }
    }
    const blockStop =
      (atk.lane > 0 ? guard.right : guard.left) *
      (0.45 + 0.55 * guard.shell) *
      (0.6 + 0.4 * clamp01(playerStamina / 100)) *
      (atk.target === 'head' ? 1 : 0.75);
    if (blockStop > 0.45 && defence > 0.35) {
      defence *= 1 - 0.75 * blockStop;
      if (!defeatedBy) defeatedBy = 'guard';
    }

    const q = clamp01(defence);
    let out: HitResult['outcome'];
    if (q >= 0.55) out = 'clean';
    else if (q >= 0.25) out = 'glancing';
    else if (defeatedBy === 'guard') out = 'blocked';
    else if (defeatedBy) out = 'slipped';
    else out = 'miss';
    return {
      outcome: out,
      quality: q,
      terms: { range: 1, lateral: 1, height: 1, defence, opening: 1, confidence: ctx.poseConfidence },
      defeatedBy,
    };
  }

  const onLine = atk.lane > 0 ? guard.right : guard.left;
  // A tired player's guard is worth less even if the pose says the hands are up:
  // this is the honest way to model fatigue without lying about what we saw.
  const staminaFactor = 0.6 + 0.4 * clamp01(playerStamina / 100);
  const guardStop = onLine * (0.5 + 0.5 * guard.shell) * staminaFactor * (atk.target === 'head' ? 1 : 0.7);
  if (guardStop > 0.3) {
    defence *= 1 - 0.85 * guardStop;
    defeatedBy = 'guard';
  }

  if (guard.evading && guard.evasionDepth > 0.3) {
    const eff = incomingEvasionEffectiveness(atk, guard);
    if (eff * guard.evasionDepth > 0.2) {
      defence *= 1 - 0.93 * eff * guard.evasionDepth;
      defeatedBy = guard.evading;
    }
  }

  const quality = clamp01(range * lateral * height * defence);
  let outcome: HitResult['outcome'];
  if (quality >= 0.55) outcome = 'clean';
  else if (quality >= 0.25) outcome = 'glancing';
  else if (defeatedBy === 'guard') outcome = 'blocked';
  else if (defeatedBy) outcome = 'slipped';
  else outcome = 'miss';

  return {
    outcome,
    quality,
    terms: { range, lateral, height, defence, opening: 1, confidence: ctx.poseConfidence },
    defeatedBy,
  };
}

function incomingEvasionEffectiveness(atk: IncomingAttack, g: GuardState): number {
  const movedAway = clamp01(Math.abs(g.headOffX - atk.lane) / 0.4);
  switch (g.evading) {
    case 'slip':
      return atk.type === 'jab' || atk.type === 'cross' || atk.type === 'overhand'
        ? 0.5 + 0.5 * movedAway
        : atk.type === 'hook'
          ? 0.2 * movedAway
          : 0.3;
    case 'duck':
      return atk.target === 'body' ? 0.05 : atk.type === 'uppercut' ? 0.1 : 0.88;
    case 'roll':
      return atk.type === 'hook' ? 0.92 : atk.type === 'uppercut' ? 0.2 : 0.5;
    case 'pull':
      // Mirrors the player-punch table: pulling is the counter to an uppercut.
      if (atk.type === 'uppercut') return 0.88;
      if (atk.type === 'jab' || atk.type === 'cross') return 0.72;
      return atk.type === 'hook' ? 0.25 : 0.4;
    default:
      return 0.25;
  }
}
